import { LazyLoadingSectionLogger, LoggingSection } from "@multiweb/logging";
import { IRowData } from "@multiweb/types/contracts";
import { CancellationToken } from "@multiweb/types/multiweb";
import { IDBDataSourceParams, IMEDataSource } from "Global/Base/DataSources/Interfaces";
import { Event as GlobalEvent } from "Global/Base/Events";
import { IDisposable } from "Global/Base/IDisposable";
import { PromiseUtils } from "Global/Base/Promises/PromiseUtils";
import { IChangedAttr, IOWObject } from "OW.Remoting/Remoting/DataTypes/IOWObject";
import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { IExtendedObjectOperation } from "OW.Remoting/Remoting/OWManagerInterfaces";
import { OWManager } from "../../../../OW.Remoting/Remoting/OWManager";
import { EditableDBDataSource, SaveResultData } from "../EditableDBDataSource";
import { MEChangeStack } from "./MEChangeStack";
import { IMultiEditObject } from "./MEOWObject";
import { IMEChangeGroup, IMEConditionGroup, IMERecordsGroup, MEConditionGroup, MERecordsGroup } from ".";

const logger = LazyLoadingSectionLogger.create(LoggingSection.multiEditing);

/**
 * DataSource podporující MultiEditaci
 */
export class MEDataSource extends EditableDBDataSource implements IMEDataSource, IDisposable {
    private _stack?: MEChangeStack;
    private _currentSelectionGroup?: IMEChangeGroup;
    private _currentObject: IOWObject;
    private __isMultiEditing: boolean = false;
    /**
    * Chain promisů s probihajicimi updaty dat
    */
    private _waitForUpdatesTask: Promise<void> = Promise.resolve();

    private get _isMultiEditing(): boolean {
        return this.__isMultiEditing;
    }
    private set _isMultiEditing(value: boolean) {
        if (value != this.__isMultiEditing) {
            this.__isMultiEditing = value;
            this.isMultiEditingChanged.trigger(this, value);
        }
    }
    public get isMultiEditing(): boolean {
        return this._isMultiEditing;
    }

    public readonly isMultiEditingChanged: GlobalEvent<IMEDataSource, boolean> = new GlobalEvent<IMEDataSource, boolean>();

    constructor(params: IDBDataSourceParams) {
        super(params);
        this._stack = new MEChangeStack(this.className, (id) => this._objects.get(id)?.actualObject?.instanceID, this._frameContext, params.composites);
    }

    public waitForPendingUpdates(): Promise<void> {
        return this._waitForUpdatesTask;
    }

    public enqueuePendingUpdateTask(task: Promise<void>): Promise<void> {
        return this._waitForUpdatesTask = this._waitForUpdatesTask.then(() => task);
    }

    private _createNewSelectionGroup(isSelectedAll?: boolean, lastId?: string, selectedRowsIds?: string[]): void {
        if (isSelectedAll) {
            //pokud předchozí výběr byl také "vybrat vše" a je potřeba překopírovat pole excluded, protože dataGrid neposílá ID odznačených řádků. Dá se k tomu dostat jen přes lastClickedRow
            let exclude: string[] = [];
            if (this._currentSelectionGroup instanceof IMEConditionGroup && lastId) {
                exclude = this._currentSelectionGroup.excluded.includes(lastId) ? this._currentSelectionGroup.excluded.filter(i => i !== lastId) : [...this._currentSelectionGroup.excluded, lastId];
            }
            this._currentSelectionGroup = new MEConditionGroup(this.condition, this.conditionParams);
            exclude.forEach(e => this._currentSelectionGroup.exclude(e));
            const g = this._currentSelectionGroup as MEConditionGroup;
            logger.debug(`Created new condition group '${g.condition}' excluding [${g.excluded.join(",")}]`);
        } else {
            this._currentSelectionGroup = new MERecordsGroup(selectedRowsIds);
            logger.debug(`Created new objects group [${(selectedRowsIds || []).join(",")}]`);
        }
    }

    //TODO: předělat na IOWObject, refaktoring se ale rozroste na většinu tříd pracujích s OWObject
    public override async getObject(id: string, extendedOperations: IExtendedObjectOperation[] | null, forceLoad: boolean = false, ct?: CancellationToken): Promise<OWObject | null> {
        const singleObjectGetter = () => {
            if (!id)
                throw new Error("[MultiEdit] unable to get object instance. No ID was specified");
            return super.getObject(id, extendedOperations, forceLoad, ct);
        };

        const obj = await this._stack.getCurrentObject(this._currentSelectionGroup, singleObjectGetter) as unknown as OWObject;

        if (obj && !obj.valueSet.containsListener(this._listenerValueSet)) {
            obj.valueSet.add(this._listenerValueSet);
        }

        this._currentObject?.valueSet?.remove(this._listenerValueSet);
        this._currentObject = obj;

        return obj;
    }

    /**
     * Zabalí původní setValue a přidá funkcionalitu pro reagování na změny atributů
     */
    private _listenerValueSet = async (obj: IOWObject, data: IChangedAttr): Promise<void> => {
        const statePromise = PromiseUtils.defer<void>();
        this.enqueuePendingUpdateTask(statePromise.promise);

        try {
            let serializedValue: any = null;

            if (data.value != null) {
                const attrInfo = await obj.getAttributeMetadata(data.attributeName);
                const serializer = await obj.getSerializers(attrInfo, { serializeWithId: true });

                serializedValue = serializer?.serializer(data.value, await obj.getValue(data.attributeName)) ?? data.value; // serializace
                if (typeof serializedValue !== "string") serializedValue = JSON.stringify(serializedValue);
            }
            this._currentSelectionGroup.addChange(data.attributeName, serializedValue);

            if (obj instanceof IMultiEditObject) {
                //pokud byla provedena hromadná změna
                this._isMultiEditing = true;
            }
            statePromise.resolve();
        } catch (e) {
            statePromise.reject();
        }
    };

    public updateSelectedGroup(isSelectedAll?: boolean, lastId?: string, selectedRowsIds?: string[]): void {
        if (this._currentSelectionGroup?.hasChanges) {
            this._stack.add(this._currentSelectionGroup, this._getRequestContext());
        }
        this._createNewSelectionGroup(isSelectedAll, lastId, selectedRowsIds);
    }

    public override async cancelChanges(key: string = "", forceRefreshCancelledRecord: boolean = false, maintainSelection: boolean = false): Promise<void> {
        this._cleanup();
        return super.cancelChanges(key, forceRefreshCancelledRecord, maintainSelection);
    }

    public override refreshData(fetchCount?: number, manualRefresh?: boolean, cancellationToken: CancellationToken = CancellationToken.none): Promise<Iterable<IRowData>> {
        this._cleanup();
        return super.refreshData(fetchCount, manualRefresh, cancellationToken);
    }

    public set requiredAttrs(requiredAttrs: Map<string, number>) {
        this._stack.requiredAttrs = requiredAttrs;
    }

    private _cleanup(): void {
        this._stack.clear();
        this._isMultiEditing = false;
        if (this._currentSelectionGroup instanceof IMERecordsGroup)
            this._currentSelectionGroup = new MERecordsGroup([...this._currentSelectionGroup.instances]);
        else if (this._currentSelectionGroup instanceof IMEConditionGroup)
            this._currentSelectionGroup = new MEConditionGroup(this._currentSelectionGroup.condition, this._currentSelectionGroup.conditionParams);
    }

    public override async saveObjects(saveOnlyKeys?: string[], forceApplyUpdates?: boolean, ct: CancellationToken = CancellationToken.none): Promise<SaveResultData[]> {
        let resultData: SaveResultData[] = [];

        await this.waitForPendingUpdates();
        const isMultiEditing = this.isMultiEditing;
        try {
            // Pokud se ukládá jeden objekt, zavolá se pro uložení objektu metoda předka
            if (!isMultiEditing) {
                resultData = await super.saveObjects(saveOnlyKeys, forceApplyUpdates, ct);
            } else {
                await Promise.all(this._waitForUpdateMultiTask);
                this._waitForUpdateMultiTask = [];
                ct.throwIfCancellationRequested();

                await this._waitForInstanceUpdates(ct);

                if (!forceApplyUpdates) {
                    if (!this._currentObject.isValid)
                        return resultData;
                }

                if (this._currentSelectionGroup?.hasChanges) {
                    await this._stack.add(this._currentSelectionGroup, this._getRequestContext());
                }

                const { messages, isInLongTransaction } = await OWManager.applyMultiEditUpdates(this._stack, this._getRequestContext(ct));

                resultData = [{ data: null, messages, isInLongTransaction }];
            }
        } finally {
            // pokud finalizuji kontext, refresh provedu až bude commitnut
            (resultData.length && isMultiEditing && !this._getRequestContext()?.getTransactionContextId()) ? this.refreshData() : this._cleanup();
            if (isMultiEditing)
                this.afterSaved.trigger(this, { saved: resultData.filter(r => r.data?.id).map(r => r.data.id) });
        }
        return resultData;
    }

    public override dispose(): void {
        super.dispose();
        this._currentObject?.valueSet?.remove(this._listenerValueSet);
    }
}
